import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { schoolAreaComplianceSupportingDataQuery } from '../generated/schoolAreaComplianceSupportingDataQuery';
import { requirementSchoolBusLinkFieldsFragment } from '../models/RequirementSchoolBusLink';
import { videoFieldsFragment } from '../models/Video';
import { blobFieldsFragment } from '../models/Blob';


/**
 * Get a the data needed by the SchoolAreaCompliance component.
 *
 * NOTE this is designed to be used in addition to useSchoolOverviewSupportingData, and so does not include any data already returned there.
 * The reason to include this as an additional request rather than have this handle everything is for performance benefits when using cached results to maximise
 * the amount of cached data used between the two screens.
 * @param options
 */
export function useSchoolAreaComplianceSupportingData(options: AsyncLoadOptions = {}): AsyncLoadResult<schoolAreaComplianceSupportingDataQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<schoolAreaComplianceSupportingDataQuery>(
        gql`
        query schoolAreaComplianceSupportingDataQuery {
            requirementSchoolBusLinks (currentRequirementsRelease: true) {
                ...requirementSchoolBusLinkFields
            }

            videos {
                ...videoFields
                thumbnailBlob {
                    ...blobFields
                }
            }
        }

        ${requirementSchoolBusLinkFieldsFragment}
        ${videoFieldsFragment}
        ${blobFieldsFragment}
        `,
        {
            variables: {
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
